import { useEffect, useRef, useState } from 'react';

import { Search, X } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { cn } from '@/lib/utils';
import { SearchFilter } from '@/types/filters';

interface IProps {
  config: SearchFilter;
  value: string;
  onChange: (key: string, value: string) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => boolean | void;
  containerClassName?: string;
}

export const FilterSearch = ({ config, value, onChange, onFocus, containerClassName }: IProps) => {
  const [input, setInput] = useState(value ?? '');
  const inputRef = useRef<HTMLInputElement>(null);

  // Use minCharacters from config or default to 0
  const minCharacters = config.minCharacters ?? 0;

  const updateQuery = useDebouncedCallback((value: string) => {
    // Only update the search if it has minimum required characters or is empty (for clearing)
    if (value.length >= minCharacters || value === '') {
      onChange(config.name, value);
    }
  }, 300);

  // Sync internal input state with external value (from searchParams)
  useEffect(() => {
    setInput(value ?? '');
  }, [value]);

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (onFocus) {
      const result = onFocus(event);
      // If onFocus returns false, blur the input
      if (result === false) {
        event.target.blur();
      }
    }
  };

  return (
    <div className={cn('relative w-64', containerClassName)}>
      <Search className="absolute left-3 top-1/2 h-3 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        ref={inputRef}
        placeholder={config.placeholder || config.label}
        value={input}
        onChange={e => {
          const value = e.target.value;
          setInput(value);
          updateQuery(value);
        }}
        onFocus={handleFocus}
        className="h-9 pl-9 pr-8"
      />
      {input && (
        <button
          type="button"
          onClick={() => {
            setInput('');
            updateQuery('');
          }}
          className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center justify-center rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};
